import { Helmet } from 'react-helmet'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { HashLink as Link } from 'react-router-hash-link'
import { motion, useAnimationControls } from 'framer-motion'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import MainHeader from '../../../components/MainHeader'
import MainFooter from '../../../components/MainFooter'
import CTAButton from '../../../components/CTAButton'
import AnimatedQuote from '../../../components/AnimatedQuote'
import PricingCard, { BundleCard } from '../../../components/PricingCard'
import { boxvar1, boxvar3, boxvar2_1, boxvar2_2, boxvar2dyndelay, oPU } from '../../../core.js'
import { faPhone, faChevronDown, faMagnifyingGlassArrowRight, faTags } from '@fortawesome/free-solid-svg-icons'
import packageJson from '../../../../package.json'

import testimonial_manuela from '../../../assets/pictures/testimonials/testimonial_manuela.webp'
import macbook_webdev from '../../../assets/pictures/sectionimages/macbook_webdev-cut_compressed.webp'

import '../../Pricing/Pricing.css'
import '../../Pricing/MPricing.css'
import '../../Home/Home.css'
import '../../Home/MHome.css'
import './FunnelVerein.css'
import './MFunnelVerein.css'
import useProgressiveHeroBg from '../../../hooks/useProgressiveHeroBg'

const scrolldowniconanim = {
    idle: {
        y: [0, 6, 0],
        scale: 1,
        transition: { duration: 1.5, repeat: Infinity, ease: 'easeOut' },
    },
    hover: {
        y: [0, 3, 0],
        scale: 1.2,
        transition: { duration: 1.0, repeat: Infinity, ease: 'easeInOut' },
    },
}

const CALENDLY = 'https://calendly.com/tonetworkde-meet/getintouch'
const STAND = packageJson.pricingdate
const SITE = 'https://tonetwork.de'

const REGIONS = {
    memmingen: {
        city: 'Memmingen',
        region: 'Unterallgäu',
        canonical: '/landing/verein-memmingen',
        seoTitle: 'Vereinswebseite Memmingen – Website & Betreuung für Vereine | ToNetwork',
        seoDescription: 'Verein-Komplett in Memmingen: Website, Logo-Auffrischung und Care-Betreuung ab 1.290 € + 29 €/Monat. ToNetwork – faire Konditionen für gemeinnützige Vereine.',
        h1: 'Vereinswebseite in Memmingen – professionell, bezahlbar, betreut',
        h1sub: 'Website, Logo und laufende Betreuung – alles, was euer Verein online braucht.',
        localLead: 'Wir unterstützen Vereine in Memmingen und im Unterallgäu mit fairen Konditionen – damit ihr euch auf euer Ehrenamt konzentrieren könnt, nicht auf Technik.',
        areaServed: ['Memmingen', 'Unterallgäu', 'Hawangen', 'Babenhausen', 'Türkheim'],
    },
    unterallgaeu: {
        city: 'Unterallgäu',
        region: 'Allgäu',
        canonical: '/landing/verein-unterallgaeu',
        seoTitle: 'Vereinswebseite Unterallgäu – Paket ab 1.290 € | ToNetwork',
        seoDescription: 'Verein-Komplett im Unterallgäu: Starter-Website, Logo-Auffrischung und Care-Betreuung. Partner aus Hawangen – transparente Preise, kostenloses Erstgespräch.',
        h1: 'Vereinswebseite im Unterallgäu – endlich online, ohne Kopfschmerzen',
        h1sub: 'Schnell online, professionell und bezahlbar – perfekt für Vereine und ehrenamtliche Auftritte.',
        localLead: 'Sportverein, Hilfsorganisation oder Kulturverein – wir kennen die Region und wissen, was Vereine brauchen: wenig Aufwand, faire Preise, fester Ansprechpartner.',
        areaServed: ['Unterallgäu', 'Memmingen', 'Türkheim', 'Mindelheim', 'Bad Wörishofen'],
    },
    allgaeu: {
        city: 'Allgäu',
        region: 'Schwaben',
        canonical: '/landing/verein-allgaeu',
        seoTitle: 'Vereinswebseite Allgäu – Website & Betreuung für Vereine | ToNetwork',
        seoDescription: 'Verein-Komplett im Allgäu: Website (Starter), Logo-Auffrischung und Care-Betreuung ab 1.290 €. ToNetwork – All-in-One für gemeinnützige Vereine.',
        h1: 'Vereinswebseite im Allgäu – digital sichtbar, ehrenamtlich entlastet',
        h1sub: 'Alles, was euer Verein online braucht – zum fairen Ehrenamtspreis.',
        localLead: 'Ob Memmingen, Kempten oder das ländliche Allgäu: Ein Ansprechpartner für Website, Logo und Betreuung – persönlich und ohne Agentur-Overhead.',
        areaServed: ['Allgäu', 'Memmingen', 'Kempten', 'Unterallgäu', 'Oberallgäu'],
    },
    kempten: {
        city: 'Kempten',
        region: 'Allgäu',
        canonical: '/landing/verein-kempten',
        seoTitle: 'Vereinswebseite Kempten – Paket für Vereine ab 1.290 € | ToNetwork',
        seoDescription: 'Verein-Komplett in Kempten und Umgebung: professionelle Vereinswebsite, Logo und Care-Betreuung. ToNetwork – persönlicher Partner aus dem Allgäu.',
        h1: 'Vereinswebseite in Kempten & Allgäu – seriös online, fair kalkuliert',
        h1sub: 'Website, Logo und Betreuung aus einer Hand – damit euer Verein gefunden wird.',
        localLead: 'Für Vereine in Kempten und der Region: kurze Wege, direkte Kommunikation und besonders faire Konditionen für gemeinnützige Organisationen.',
        areaServed: ['Kempten', 'Allgäu', 'Memmingen', 'Sonthofen', 'Immenstadt'],
    },
    default: {
        city: 'Memmingen',
        region: 'Allgäu',
        canonical: '/funnel/verein',
        seoTitle: 'Vereinswebseite – Website, Logo & Betreuung für Vereine | ToNetwork',
        seoDescription: 'Verein-Komplett von ToNetwork: Website (Starter), Logo-Auffrischung und Care-Betreuung ab 1.290 € + 29 €/Monat. Für Vereine in Memmingen, Unterallgäu und Allgäu.',
        h1: 'Euer Verein verdient eine professionelle Webseite',
        h1sub: 'Website, Logo und Betreuung – alles, was ihr online braucht, zum fairen Ehrenamtspreis.',
        localLead: 'Aus Hawangen im Unterallgäu betreuen wir Vereine in Memmingen, im Allgäu und darüber hinaus – mit festem Ansprechpartner und besonders fairen Konditionen.',
        areaServed: ['Memmingen', 'Unterallgäu', 'Allgäu', 'Kempten'],
    },
}

function resolveRegion(pathname) {
    if (pathname.includes('memmingen')) return REGIONS.memmingen
    if (pathname.includes('unterallgaeu')) return REGIONS.unterallgaeu
    if (pathname.includes('allgaeu')) return REGIONS.allgaeu
    if (pathname.includes('kempten')) return REGIONS.kempten
    return REGIONS.default
}

const bundleVerein = {
    name: 'Verein-Komplett',
    featured: true,
    price: 'ab 1.290 €',
    priceExtra: '+ 29 € / Monat',
    contains: 'Website (Starter) + Logo-Auffrischung + Care-Betreuung',
    sales: 'Alles, was euer Verein online braucht – zum fairen Ehrenamtspreis.',
    paket: 'bundle-verein',
}

const webStarter = {
    name: 'Starter',
    badge: 'Einzeln buchbar',
    price: 'ab 990 €',
    priceNote: 'einmalig',
    forwho: 'Vereine & kleine Auftritte',
    sales: 'Schnell online, professionell und bezahlbar – perfekt für Vereine und alle, die endlich einen neuen Online-Auftritt wollen, ohne dass es kompliziert wird.',
    features: [
        { t: 'bis 3 Seiten', in: null },
        { t: 'Design: Vorlage, individuell angepasst', in: null },
        { t: 'Mobiloptimiert', in: true },
        { t: 'SEO-Grundlagen', in: true },
        { t: 'Kontaktformular', in: false },
        { t: 'Blog / News', in: false },
        { t: 'Shop / Buchung / Login', in: false },
        { t: 'fertig in ~2 Wochen', in: null },
    ],
    paket: 'web-starter',
}

const care = {
    name: 'Care',
    price: '39 €',
    priceNote: 'pro Monat',
    forwho: 'Grundabsicherung',
    sales: 'Deine Website immer sicher, aktuell und schnell – wir kümmern uns, oft bevor du es merkst.',
    features: [
        { t: 'Hosting in Deutschland', in: true },
        { t: 'SSL & Sicherheits-Updates', in: true },
        { t: 'Monitoring (24/7)', in: true },
        { t: 'Regelmäßige Backups', in: false },
        { t: '.de Domain inklusive', in: false },
        { t: 'Kleine Änderungen inklusive', in: false },
        { t: 'Reaktionszeit innerhalb von 3 Werktagen (7-20 Uhr)', in: null },
        { t: 'Fester Ansprechpartner', in: true },
        { t: 'Monatlich kündbar', in: true },
    ],
    paket: 'care',
}

const painPoints = [
    {
        title: 'Die Vereinswebsite ist veraltet – oder fehlt ganz',
        text: 'Mitglieder und Spender finden nichts Aktuelles online. Der Vorstand hat keine Zeit, sich mit Technik zu beschäftigen – und niemand im Verein weiß, wo man anfangen soll.',
    },
    {
        title: 'Ehrenamt + IT geht selten gut zusammen',
        text: 'Updates, Sicherheit, Hosting – das alles landet irgendwann auf dem Schreibtisch des IT-begeisterten Vorstandsmitglieds. Bis es das auch nicht mehr schafft.',
    },
    {
        title: 'Budget ist knapp, Ansprüche trotzdem hoch',
        text: 'Professionell wirken, aber nicht Unsummen ausgeben. Die meisten Agenturen sind für Vereinsbudgets nicht gemacht – ihr braucht jemanden, der das versteht.',
    },
]

const faqsForRegion = (city) => [
    {
        q: `Was kostet eine Vereinswebseite in ${city}?`,
        a: 'Unser Bundle „Verein-Komplett" startet ab 1.290 € einmalig plus 29 €/Monat für Care-Betreuung. Alle Preise sind Listenpreise ohne Rabatte – nach einem kostenlosen Erstgespräch erhältst du ein individuelles Festangebot. Für gemeinnützige Vereine gelten besonders faire Konditionen.',
    },
    {
        q: `Betreut ihr auch Vereine außerhalb von ${city}?`,
        a: 'Ja. Wir sitzen in Hawangen bei Memmingen und arbeiten mit Vereinen im gesamten Unterallgäu, Allgäu und darüber hinaus – vor Ort oder per Video.',
    },
    {
        q: 'Bin ich an die monatliche Betreuung gebunden?',
        a: 'Nein. Care ist monatlich kündbar. Wir wollen euch überzeugen, nicht festhalten.',
    },
    {
        q: 'Kann ich einzelne Leistungen statt des Bundles buchen?',
        a: 'Ja. Website (Starter), Logo-Auffrischung und Care sind auch einzeln buchbar. Das Bundle „Verein-Komplett" ist unser Rundum-Paket zum fairen Ehrenamtspreis.',
    },
]

function localBusinessJsonLd(region) {
    return {
        '@context': 'https://schema.org',
        '@type': 'ProfessionalService',
        name: 'ToNetwork UG (haftungsbeschränkt)',
        description: region.seoDescription,
        url: SITE,
        telephone: '+49-8332-4908872',
        email: 'hey@tonetwork.de',
        address: {
            '@type': 'PostalAddress',
            streetAddress: 'Hawangen',
            addressLocality: 'Hawangen',
            postalCode: '87749',
            addressRegion: 'Bayern',
            addressCountry: 'DE',
        },
        geo: {
            '@type': 'GeoCoordinates',
            latitude: 47.998,
            longitude: 10.283,
        },
        areaServed: region.areaServed.map((name) => ({ '@type': 'City', name })),
        priceRange: '€',
        knowsAbout: ['Vereinswebseiten', 'Webentwicklung', 'Website-Betreuung', 'Branding', 'Hosting'],
    }
}

export default function FunnelVerein() {
    const { pathname } = useLocation()
    const region = resolveRegion(pathname)
    const canonicalUrl = SITE + region.canonical
    const faqs = faqsForRegion(region.city)

    const controls = useAnimationControls()

    useEffect(() => {
        controls.start('idle')
    }, [controls])

    useProgressiveHeroBg('sec_start--funnel-verein')

    return (
        <div className="mainCon funnel_verein" id="mainCon--funnel-verein">

            <MainHeader funnel disableanimation={false} />

            <Helmet>
                <meta charSet="utf-8" />
                <title>{region.seoTitle}</title>
                <link rel="canonical" href={canonicalUrl} />
                <meta name="description" content={region.seoDescription} />
                <meta name="keywords" content={`Vereinswebseite ${region.city}, Website Verein ${region.city}, Webdesign Verein ${region.region}, Verein-Komplett, ToNetwork`} />
                <meta property="og:title" content={region.seoTitle} />
                <meta property="og:description" content={region.seoDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="de_DE" />
                <script type="application/ld+json">{JSON.stringify(localBusinessJsonLd(region))}</script>
            </Helmet>

            <section className="sec_start progressive-hero-bg" id="sec_start--funnel-verein">

                <motion.div
                    variants={boxvar1}
                    initial="hidden"
                    exit="hidden"
                    whileInView="visible"
                    viewport={{ once: false, margin: '-50px' }}
                    className="start_heading_cont"
                >
                    <AnimatedQuote className="start_quote" text={region.h1} />

                    <p className="start_about">
                        {region.h1sub}<br />
                        <br />
                        {region.localLead}<br />
                        <br />
                        Unser Bundle: <b className="start_about_highlight">Verein-Komplett</b> – Website, Logo & Care ab 1.290 € + 29 €/Monat
                    </p>
                </motion.div>

                <motion.div variants={boxvar3} initial="hidden" exit="hidden" whileInView="visible" viewport={{ once: false, margin: '-50px' }}>
                    <div className="sec_start_cta_row">
                        <CTAButton className="sec_start_btn" showArrow={false} customIcon={faPhone} iconLeft={true} title="Kostenloses Erstgespräch" onclick={() => { oPU(CALENDLY) }} />
                        <CTAButton className="sec_start_btn sec_start_btn-chat" showArrow={true} lightcta={true} title="Paket anfragen" href="/support?paket=bundle-verein" />
                    </div>
                </motion.div>

                <Link smooth to="#sec_next--funnel-verein">
                    <motion.div
                        className="sec_next_icon_div noselect"
                        onHoverStart={() => controls.start('hover')}
                        onHoverEnd={() => controls.start('idle')}
                    >
                        <span>Erfahre mehr</span>
                        <motion.div variants={scrolldowniconanim} animate={controls}>
                            <FontAwesomeIcon icon={faChevronDown} className="sec_next_icon_arrow" />
                        </motion.div>
                    </motion.div>
                </Link>

            </section>

            <section className="sec_next" id="sec_next--funnel-verein">
                <div className="sec_next_cont" id="sec_next--funnel-verein--cont">

                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_verein_section"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Kommt euch das bekannt vor?</h2>
                        <h3>Drei Probleme, die Vereine in {region.city} und Umgebung kennen.</h3>
                        <div className="funnel_verein_pain_grid">
                            {painPoints.map((p, i) => (
                                <motion.div
                                    key={p.title}
                                    variants={boxvar2dyndelay}
                                    custom={0.15 * (i + 1)}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: false, margin: '-50px' }}
                                    className="funnel_verein_pain_card"
                                >
                                    <span className="funnel_verein_pain_num">{String(i + 1).padStart(2, '0')}</span>
                                    <h4>{p.title}</h4>
                                    <p>{p.text}</p>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_verein_section pricing_section"
                        id="funnel-verein-angebot"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Die Lösung: Verein-Komplett</h2>
                        <h3>Website, Logo-Auffrischung und laufende Betreuung – aus einer Hand.</h3>
                        <p className="pricing_lead">
                            Statt euch mit Technik herumzuschlagen, bekommt ihr bei ToNetwork alles in einem Paket:
                            eine professionelle Vereinswebsite, die auf dem Handy funktioniert, ein frisches Logo
                            und monatliche Betreuung mit festem Ansprechpartner – Hosting in Deutschland inklusive.
                        </p>
                        <div className="pricing_grid funnel_verein_bundle_grid">
                            <BundleCard bundle={bundleVerein} delay={0.15} />
                        </div>
                        <p className="pricing_note">
                            Alle Preise netto, zzgl. MwSt. – Listenpreise ohne Rabatte. Jedes Paket wird individuell auf euer Projekt zugeschnitten. Stand: {STAND}
                        </p>
                    </motion.div>

                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_verein_section pricing_section"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Oder einzeln buchen</h2>
                        <h3>Flexibel starten – jederzeit erweiterbar.</h3>
                        <div className="pricing_grid">
                            <PricingCard tier={webStarter} delay={0.15} />
                            <PricingCard tier={care} delay={0.3} />
                        </div>
                        <p className="pricing_note">
                            Alle Preise netto, zzgl. MwSt. – Listenpreise ohne Rabatte. Jedes Paket wird individuell auf euer Projekt zugeschnitten. Stand: {STAND}
                        </p>
                        <br />
                        <CTAButton
                            showArrow={false}
                            customIcon={faTags}
                            iconLeft={true}
                            title="Alle weiteren Pakete & Preise"
                            href="/pricing"
                        />
                    </motion.div>

                    <motion.div
                        variants={boxvar2_2}
                        className="funnel_verein_section funnel_verein_why"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <div className="funnel_verein_why_text">
                            <h2>Warum ToNetwork?</h2>
                            <h3>Weil ihr bei uns alles bekommt – aus einer Hand.</h3>
                            <p>
                                Wir sind kein anonymer Online-Dienstleister, sondern euer persönlicher Partner aus dem Allgäu.
                                Webentwicklung, Hosting und Betreuung unter einem Dach – mit direkter Kommunikation
                                und besonderem Verständnis für ehrenamtliche Organisationen.
                            </p>
                            <ul className="funnel_verein_benefits">
                                <li>Besonders faire Konditionen für gemeinnützige Vereine</li>
                                <li>Hosting in Deutschland – DSGVO-konform</li>
                                <li>Care monatlich kündbar – ab 29 €/Monat im Vereins-Bundle</li>
                                <li>Erstberatung kostenlos, Festangebot ohne versteckte Kosten</li>
                                <li>Fester Ansprechpartner, der euch beim Namen kennt</li>
                            </ul>
                            <CTAButton
                                showArrow={false}
                                customIcon={faMagnifyingGlassArrowRight}
                                iconLeft={true}
                                title="Alle Leistungen ansehen"
                                href="/services"
                            />
                        </div>
                        <img
                            src={macbook_webdev}
                            alt={`Vereinswebseite und Webentwicklung für Vereine in ${region.city}`}
                            className="funnel_verein_why_img"
                            loading="lazy"
                        />
                    </motion.div>

                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_verein_section"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Das sagen unsere Kunden</h2>
                        <div className="funnel_verein_testimonial">
                            <span className="funnel_verein_testimonial_quote">
                                „Für das Ehrenamt war die Plattform Helferzeit eine echte Erleichterung."
                            </span>
                            <div className="funnel_verein_testimonial_person">
                                <img src={testimonial_manuela} alt="Manuela Walcher, Projekt Vesperkirche Diakonie Allgäu" />
                                <div>
                                    <span className="funnel_verein_testimonial_name">Manuela Walcher</span>
                                    <span className="funnel_verein_testimonial_company">Projekt Vesperkirche, Diakonie Allgäu</span>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_verein_section pricing_section"
                        id="funnel-verein-faq"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Häufige Fragen</h2>
                        <div className="pricing_faq_list">
                            {faqs.map((f, i) => (
                                <details key={i} className="pricing_faq_item" open={i === 0}>
                                    <summary>{f.q}</summary>
                                    <p>{f.a}</p>
                                </details>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div
                        variants={boxvar2dyndelay}
                        className="small_services_box small_services_box-bigbottom funnel_verein_cta_box"
                        initial="hidden"
                        whileInView="visible"
                        custom={0.3}
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <div className="small_services_textbox">
                            <h3>Bereit für euren Verein in {region.city}?</h3>
                            <br />
                            <p>
                                In einem kostenlosen 30-Minuten-Gespräch finden wir heraus, was euer Verein wirklich
                                braucht – ehrlich und ohne Verkaufsdruck. Alle Preise hier sind Listenpreise ohne Rabatte
                                und werden individuell für euch ausgestaltet.
                            </p>
                            <CTAButton
                                showArrow={false}
                                customIcon={faPhone}
                                iconLeft={true}
                                title="Kostenloses Erstgespräch"
                                onclick={() => { oPU(CALENDLY) }}
                            />
                            <br />
                            <p className="pricing_toni_hint">
                                Erst Orientierung gefragt? <a href="/chat">ToNi beantwortet Fragen zu Paketen und Preisen</a> – unverbindlich im Chat.
                            </p>
                        </div>
                    </motion.div>

                    <p className="pricing_footnote">
                        Es gelten unsere <a href="/rechtliches/agb">Allgemeinen Geschäftsbedingungen (AGB)</a>
                    </p>

                    <br /><br /><br />
                </div>
            </section>

            <MainFooter />
        </div>
    )
}
